import React, { useState } from "react";
import { Grid, List, ListItemIcon, ListItemButton, ListItemText, Badge, styled } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Mail, Send, Drafts, Delete, ArrowRight } from "@mui/icons-material";
import { Email, EmailNav } from "../emailTypes";
import { getUnreadMails } from "../emailHelpers";
import { useIntl } from "react-intl";
import { isMobile, isDesktop } from "react-device-detect";
import EmailCardListing from "./EmailCardListing";
import { MobileHeadBarVariant } from "./HeadBar.mobile";

interface Props {
	emails: Email[];
	selectedMail?: string;
	onShowEmail: (emailId: string) => void;
	setAction: (newState: string) => void;
	setMobileHeadbarVariant: (newState: MobileHeadBarVariant) => void;
	setEmailSubject: (newState: string) => void;
	action: string;
	mobileHeadbarVariant: string;
}

const EmailSidebar: React.FC<Props> = ({
	setAction,
	setMobileHeadbarVariant,
	setEmailSubject,
	action,
	mobileHeadbarVariant,
	...props
}) => {
	const classes = useStyles();
	const { emails, selectedMail, onShowEmail } = props;
	const intl = useIntl();
	const [selectedNav, setSelectedNav] = useState<string>(EmailNav.INBOX);

	const unreadEmailCount = getUnreadMails(emails).length;

	const onShowAction = (action) => {
		setSelectedNav(EmailNav[action.toUpperCase()]);
		if (isMobile) {
			setAction(intl.formatMessage({ id: `EMAIL.sidebar.${action}` }));
			setMobileHeadbarVariant(MobileHeadBarVariant.EMAILLISTING);
		}
	};

	return (
		<>
			<Grid container direction="row" style={{ height: "100%" }}>
				{((isMobile && action === intl.formatMessage({ id: "EMAIL.headbar.mobile.mailbox" })) || isDesktop) && (
					<Grid item md={5} xs={12} className={classes.container}>
						<MenuList>
							<StyledListItem
								id="section-email-inbox"
								onClick={() => onShowAction(EmailNav.INBOX)}
								selected={selectedNav === EmailNav.INBOX}
							>
								{isDesktop && (
									<ItemIcon>
										<Mail />
									</ItemIcon>
								)}
								<div className={classes.listSubContainer}>
									<ListItemText
										className={classes.text}
										primary={intl.formatMessage({ id: "EMAIL.sidebar.inbox" })}
									/>

									<Badge
										badgeContent={unreadEmailCount}
										color="error"
										showZero={false}
										overlap="rectangular"
										classes={{ badge: classes.badge }}
									/>
								</div>
								{isMobile && <ArrowRight />}
							</StyledListItem>
							<StyledListItem
								id="section-email-sent"
								onClick={() => onShowAction(EmailNav.OUTBOX)}
								selected={selectedNav === EmailNav.OUTBOX}
							>
								{isDesktop && (
									<ItemIcon>
										<Send />
									</ItemIcon>
								)}
								<ListItemText
									className={classes.text}
									primary={intl.formatMessage({ id: "EMAIL.sidebar.outbox" })}
								/>
								{isMobile && <ArrowRight />}
							</StyledListItem>
							<StyledListItem
								id="section-email-draft"
								onClick={() => onShowAction(EmailNav.DRAFT)}
								selected={selectedNav === EmailNav.DRAFT}
							>
								{isDesktop && (
									<ItemIcon>
										<Drafts />
									</ItemIcon>
								)}
								<ListItemText
									className={classes.text}
									primary={intl.formatMessage({ id: "EMAIL.sidebar.draft" })}
								/>
								{isMobile && <ArrowRight />}
							</StyledListItem>
							<StyledListItem
								id="section-email-trash"
								onClick={() => onShowAction(EmailNav.TRASH)}
								selected={selectedNav === EmailNav.TRASH}
							>
								{isDesktop && (
									<ItemIcon>
										<Delete />
									</ItemIcon>
								)}
								<ListItemText
									className={classes.text}
									primary={intl.formatMessage({ id: "EMAIL.sidebar.trash" })}
								/>
								{isMobile && <ArrowRight />}
							</StyledListItem>
						</MenuList>
					</Grid>
				)}
				{((isMobile &&
					action !== intl.formatMessage({ id: "EMAIL.headbar.mobile.mailbox" }) &&
					mobileHeadbarVariant === MobileHeadBarVariant.EMAILLISTING) ||
					isDesktop) && (
					<EmailCardListing
						emails={emails}
						selectedMail={selectedMail}
						onShowEmail={onShowEmail}
						setMobileHeadbarVariant={setMobileHeadbarVariant}
						setEmailSubject={setEmailSubject}
						selectedNav={selectedNav}
					/>
				)}
			</Grid>
		</>
	);
};

const ItemIcon = styled(ListItemIcon)(({ theme }) => ({
	color: "#3C4256",
	minWidth: 40,
	[theme.breakpoints.down("mobile")]: {},
}));

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
	borderRadius: isMobile ? 0 : 5,
	marginBottom: isMobile ? 0 : 8,

	// nth last child don't add border
	borderBottom: isMobile ? "1px solid #E4E5E9" : "none",
	fontWeight: 500,

	"&:last-child": {
		borderBottom: "none",
	},

	padding: isMobile ? "20px 16px" : "9px 12px",
}));

const MenuList = styled(List)(({ theme }) => ({
	padding: isMobile ? 0 : 20,
	margin: isMobile ? 16 : 0,
	borderRadius: isMobile ? 8 : 0,
	border: isMobile ? "1px solid #E4E5E9" : "none",
	backgroundColor: "#fff",
}));

export default EmailSidebar;

const useStyles = makeStyles((theme) => ({
	container: {},
	listContainer: {
		padding: "16px",
		[theme.breakpoints.down("mobile")]: {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			padding: "23px 16px",
		},
	},
	listSubContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flex: 1,
		// [theme.breakpoints.up("desktop")]: { width: "63%", maxWidth: "90px" },
		[theme.breakpoints.down("mobile")]: { columnGap: 20 },
	},
	text: {
		"& span": {
			display: "block",
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
			minWidth: 0,
			fontSize: "0.75rem",
			fontWeight: 500,
			[theme.breakpoints.down("mobile")]: { fontWeight: "bold", fontSize: "0.875rem" },
		},
	},
	badge: {
		// width: "32px",
		borderRadius: 4,
		right: 5,
		[theme.breakpoints.down("mobile")]: {
			borderRadius: 5,
			right: 12,
			width: 24,
			opacity: 0.8,
		},
	},
}));
